import api          from '../../../api';
import React        from 'react';
import utils        from '../../../utils';
import values       from '../../../values';
import Context      from '../../../context';
import DriverScreen from '..';

import { TModal                 } from '../../../types/common.type';
import { useQuery               } from 'react-query';
import { DeleteModal            } from '../../../components';
import { ICreateAndUpdateDriver } from '../../../types/driver.interface';
import {
  IBlockUser          ,
  IUnBlockUser        ,
  IFindUserInformation,
  IPayAllDriverFactors,
} from '../../../types/common.interface';

const AdministratorDriverContainer: React.FC = (): React.ReactElement => {

  // # =================================================================================================================
  // # --------------------------------------------- Start Validate Values ---------------------------------------------

  const [showModal, setShowModal] = React.useState<TModal>("NONE");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [selectedDriver, setSelectedDriver] = React.useState<any>();
  // ? Carrying last year's drivers into the selected year: the checkbox that also lists them, and the rows ticked
  // ? for transfer. Both are cleared by a successful transfer.
  const [includePreviousYear, setIncludePreviousYear] = React.useState<boolean>(false);
  const [selectedDriverIds, setSelectedDriverIds] = React.useState<string[]>([]);
  const [requestDriver, setRequestDriver] =
    React.useState<IFindUserInformation>({
      page: 1,
      limit: values.constants.PANEL_TABLE_DEFAULT_LIMIT,
    });

  // # ---------------------------------------------- End Validate Values ----------------------------------------------
  // # =================================================================================================================

  // ! =================================================================================================================
  // ! ------------------------------------------------ Start Call API -------------------------------------------------

  // ? Panel-wide academic year (سال تحصیلی). It has to be part of the query key as well as the request, otherwise
  // ? react-query serves the previous year's cached rows when the year is switched.
  const { selectedYear } = React.useContext(Context.AcademicYear);

  const resultDriver = useQuery(
    [
      values.useQueryKeys.getAllDriver,
      requestDriver?.page,
      requestDriver?.search?.value,
      requestDriver?.filters,
      selectedYear?._id,
      includePreviousYear,
    ],
    () =>
      api.post.common.findDriver({
        page        : requestDriver?.page   ,
        limit       : requestDriver?.limit  ,
        search      : requestDriver?.search ,
        filters     : requestDriver?.filters,
        academicYear: selectedYear?._id     ,
        includePreviousYear                 ,
      })
  );
  const { refetch } = resultDriver;

  // ! ------------------------------------------------- End Call API --------------------------------------------------
  // ! =================================================================================================================

  // * =================================================================================================================
  // * -------------------------------------------- Start Local Functions ----------------------------------------------

  async function transferFunction(driverIds: string[]) {
    setIsLoading(true);
    const result = await api.post.common.transferDriversToAcademicYear({
      driverIds,
      academicYear: String(selectedYear?._id),
    });
    if (result) {
      utils.successHandler(Number(result.CODE));
      setSelectedDriverIds([]);
      refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }

  async function createFunction(formData: ICreateAndUpdateDriver): Promise<boolean> {
    setIsLoading(true);
    const result = await api.post.common.createDriverByAdmin(formData);
    if (result) {
      utils.successHandler(Number(result.CODE));
      await resultDriver.refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function updateFunction(formData: ICreateAndUpdateDriver): Promise<boolean> {
    setIsLoading(true);
    const result = await api.put.common.updateDriverByAdmin(formData);
    if (result) {
      utils.successHandler(Number(result.CODE));
      await resultDriver.refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function deleteFunction(userId: string): Promise<boolean> {
    setIsLoading(true);
    const result = await api.dele.common.deleteDriver(userId);
    if (result) {
      utils.successHandler(Number(result.CODE));
      await resultDriver.refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function approveFunction(userId: string): Promise<boolean> {
    setIsLoading(true);
    const result = await api.put.common.approveDriver(userId);
    if (result) {
      utils.successHandler(Number(result.CODE));
      await resultDriver.refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function blockFunction(props: IBlockUser): Promise<boolean> {
    setIsLoading(true);
    const result = await api.post.administrator.blockUser(props);
    if (result) {
      utils.successHandler(Number(result.CODE));
      await resultDriver.refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function unBlockFunction(props: IUnBlockUser): Promise<boolean> {
    setIsLoading(true);
    const result = await api.dele.administrator.unBlock(props);
    if (result) {
      utils.successHandler(Number(result.CODE));
      await resultDriver.refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function approveDocumentsFunction(driverId: string): Promise<boolean> {
    setIsLoading(true);
    const result = await api.put.administrator.approveDriverDocuments(driverId);
    if (result) {
      utils.successHandler(Number(2010));
      await refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function payFunction(props: IPayAllDriverFactors) {
    setIsLoading(true);
    const result = await api.post.common.payAllDriverFactors(props);
    if (result) {
      utils.successHandler(Number(2010));
      await refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }


  // * --------------------------------------------- End Local Functions -----------------------------------------------
  // * =================================================================================================================

  // ? =================================================================================================================
  // ? ----------------------------------------------- Start Return View -----------------------------------------------

  return (
    <Context.DriverScreen.Provider
      value={{
        refetch,
        showModal,
        isLoading,
        setShowModal,
        setIsLoading,
        requestDriver,
        blockFunction,
        createFunction,
        updateFunction,
        deleteFunction,
        selectedDriver,
        approveFunction,
        unBlockFunction,
        setRequestDriver,
        setSelectedDriver,
        approveDocumentsFunction,
        transferFunction,
        selectedDriverIds,
        setSelectedDriverIds,
        includePreviousYear,
        setIncludePreviousYear,
        payFunction
      }}
    >
      <DriverScreen resultDriver={resultDriver} role="ADMIN" />
      <DeleteModal
        data    = { selectedDriver                   }
        user    = { selectedDriver                   }
        open    = { showModal === "DELETE"           }
        loading = { isLoading                        }
        onClose = { () => setShowModal("NONE") }
        onSubmit= { deleteFunction                   }
      />
    </Context.DriverScreen.Provider>
  );

  // ? ------------------------------------------------ End Return View ------------------------------------------------
  // ? =================================================================================================================

};

export default AdministratorDriverContainer;
